import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { 
  Settings2, 
  Plus, 
  Save, 
  Trash2, 
  Loader2,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  Calendar,
  ShoppingCart,
  HelpCircle,
  HeadphonesIcon,
  Copy,
  ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ARCHETYPE_ICONS = {
  BOOKING: Calendar,
  PURCHASE: ShoppingCart,
  ENQUIRY: HelpCircle,
  SUPPORT: HeadphonesIcon
};

const ARCHETYPE_COLORS = {
  BOOKING: 'bg-blue-100 text-blue-700 border-blue-200',
  PURCHASE: 'bg-green-100 text-green-700 border-green-200',
  ENQUIRY: 'bg-purple-100 text-purple-700 border-purple-200',
  SUPPORT: 'bg-orange-100 text-orange-700 border-orange-200'
};

// Archetype → Flow Type mapping (Utility = transactional, Marketing = promotional)
const ARCHETYPE_FLOW_TYPE = {
  BOOKING:  { label: 'Utility',   color: 'bg-blue-100 text-blue-700',   template: 'giri_utility_main' },
  PURCHASE: { label: 'Marketing', color: 'bg-pink-100 text-pink-700',    template: 'giri_univaersal_handler' },
  ENQUIRY:  { label: 'Utility',   color: 'bg-blue-100 text-blue-700',   template: 'giri_utility_main' },
  SUPPORT:  { label: 'Utility',   color: 'bg-blue-100 text-blue-700',   template: 'giri_utility_main' },
};

// Category → Group mapping
const CATEGORY_GROUPS = {
  'Product': [
    'Arts, Craft & Books', 'Daily Essentials', 'Deities', 'Deities & Paraphernalia',
    'Devotional Items', 'Electronics & Instruments', 'Fashion', 'Food and Wellness',
    'Home, Kitchen & Living', 'Kids / Toys / Games', 'Men\'s Fashion', 'Mobiles, Computers',
    'Prasadam', 'Sports, Fitness & Travel', 'Women\'s Fashion',
  ],
  'Service': [
    'Astrology', 'Digital Services', 'Health & Fitness', 'Health & Wellness',
    'Home Utilities', 'Kirtan & Arts', 'Professional Services',
  ],
  'Rental': [
    'Car, Motorbike, Industrial', 'Event Rental',
  ],
  'Subscription': [
    'Learn', 'Learning',
  ],
  'Event': [
    'Plan Events', 'Yatras',
  ],
  'Travel': [
    'Travel & Stay',
  ],
  'Volunteering': [
    'Others',
  ],
};

const getGroupForCategory = (cat) => {
  for (const [group, cats] of Object.entries(CATEGORY_GROUPS)) {
    if (cats.includes(cat)) return group;
  }
  return 'Others';
};

export function FlowConfigurator() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [archetypes, setArchetypes] = useState([]);
  const [flowConfigs, setFlowConfigs] = useState([]);
  
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [selectedArchetype, setSelectedArchetype] = useState('ENQUIRY');
  const [configData, setConfigData] = useState({
    labels: {},
    options: {},
    services: [],
    products: [],
    prices: {}
  });
  
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [sortMode, setSortMode] = useState('group'); // 'group' | 'alpha'

  // Grouped categories for the selector
  const groupedCategories = Object.keys(CATEGORY_GROUPS).reduce((acc, group) => {
    const matched = CATEGORY_GROUPS[group].filter(c => categories.includes(c));
    if (matched.length > 0) acc[group] = matched;
    return acc;
  }, {});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch subcategories
      const subcatRes = await fetch(`${API_URL}/api/flows/subcategories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const subcatData = await subcatRes.json();
      setSubcategories(subcatData.subcategories);
      setCategories(subcatData.categories);

      // Fetch archetypes
      const archRes = await fetch(`${API_URL}/api/flows/archetypes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const archData = await archRes.json();
      setArchetypes(archData.archetypes);

      // Fetch existing configs
      const configRes = await fetch(`${API_URL}/api/flows/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const configData = await configRes.json();
      setFlowConfigs(configData.flow_configs);
      
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const filteredSubcategories = selectedCategory
    ? subcategories.filter(s => s.category === selectedCategory)
    : subcategories;

  const handleCreateConfig = async () => {
    if (!selectedSubcategory) {
      setError('Please select a subcategory');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/api/flows/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subcategory_id: selectedSubcategory.id,
          archetype: selectedArchetype,
          config: configData
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create configuration');
      }

      setSuccess(`Configuration created! Flow Token: ${data.flow_token}`);
      setFlowConfigs([...flowConfigs, data.flow_config]);
      setShowCreateForm(false);
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateConfig = async (configId, subcategoryId, updates) => {
    setSaving(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/flows/config/${subcategoryId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to update configuration');
      }

      setFlowConfigs(flowConfigs.map(c => 
        c.id === configId ? data.flow_config : c
      ));
      setSuccess('Configuration updated');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfig = async (subcategoryId) => {
    if (!window.confirm('Are you sure you want to delete this configuration?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/flows/config/${subcategoryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to delete configuration');
      }

      setFlowConfigs(flowConfigs.filter(c => c.subcategory_id !== subcategoryId));
      setSuccess('Configuration deleted');
    } catch (err) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    setSelectedCategory('');
    setSelectedSubcategory(null);
    setSelectedArchetype('ENQUIRY');
    setConfigData({ labels: {}, options: {}, services: [], products: [], prices: {} });
  };

  const copyFlowToken = (vendorId, subcatId) => {
    const token = `vendor_${vendorId}_subcat_${subcatId}`;
    navigator.clipboard.writeText(token);
    setSuccess('Flow token copied to clipboard!');
    setTimeout(() => setSuccess(''), 3000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#00D95F]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="flow-configurator">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-[#00D95F]" />
            WhatsApp Flow Configurator
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Configure dynamic WhatsApp Flows for your business
          </p>
        </div>
        
        {!showCreateForm && (
          <Button 
            onClick={() => setShowCreateForm(true)}
            className="bg-[#00D95F] hover:bg-[#00C054] gap-2"
          >
            <Plus className="w-4 h-4" />
            Create New Flow
          </Button>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-600 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5" />
          {success}
        </div>
      )}

      {/* Create New Flow Form */}
      {showCreateForm && (
        <Card className="p-6 bg-white border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Flow Configuration</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Category Selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Category</Label>
                <div className="flex gap-1 text-xs">
                  <button
                    type="button"
                    onClick={() => setSortMode('group')}
                    className={`px-2 py-0.5 rounded ${sortMode === 'group' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600'}`}
                  >By Type</button>
                  <button
                    type="button"
                    onClick={() => setSortMode('alpha')}
                    className={`px-2 py-0.5 rounded ${sortMode === 'alpha' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600'}`}
                  >A-Z</button>
                </div>
              </div>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedSubcategory(null);
                }}
              >
                <option value="">All Categories</option>
                {sortMode === 'alpha'
                  ? categories.sort().map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))
                  : Object.entries(groupedCategories).map(([group, cats]) => (
                      <optgroup key={group} label={`── ${group} ──`}>
                        {cats.sort().map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </optgroup>
                    ))
                }
              </select>
            </div>

            {/* Subcategory Selection */}
            <div className="space-y-2">
              <Label>Subcategory *</Label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={selectedSubcategory?.id || ''}
                onChange={(e) => {
                  const subcat = subcategories.find(s => s.id === parseInt(e.target.value));
                  setSelectedSubcategory(subcat);
                }}
              >
                <option value="">Select Subcategory</option>
                {filteredSubcategories.map(subcat => (
                  <option key={subcat.id} value={subcat.id}>
                    {subcat.name} ({subcat.category})
                  </option>
                ))}
              </select>
            </div>

            {/* Archetype Selection */}
            <div className="space-y-2 md:col-span-2">
              <Label>Flow Type (Archetype) *</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {archetypes.map(arch => {
                  const Icon = ARCHETYPE_ICONS[arch];
                  const isSelected = selectedArchetype === arch;
                  return (
                    <button
                      key={arch}
                      type="button"
                      onClick={() => setSelectedArchetype(arch)}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        isSelected 
                          ? 'border-[#00D95F] bg-green-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon className={`w-6 h-6 mx-auto mb-2 ${isSelected ? 'text-[#00D95F]' : 'text-gray-400'}`} />
                      <p className={`text-sm font-medium ${isSelected ? 'text-[#00D95F]' : 'text-gray-700'}`}>
                        {arch}
                      </p>
                    </button>
                  );
                })}
              </div>
              {/* Auto-derived Flow Channel label */}
              {selectedArchetype && ARCHETYPE_FLOW_TYPE[selectedArchetype] && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-gray-500">WhatsApp Channel:</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${ARCHETYPE_FLOW_TYPE[selectedArchetype].color}`}>
                    {ARCHETYPE_FLOW_TYPE[selectedArchetype].label}
                  </span>
                  <span className="text-xs text-gray-400">
                    Template: {ARCHETYPE_FLOW_TYPE[selectedArchetype].template}
                  </span>
                </div>
              )}
            </div>

            {/* Dynamic Config Fields based on Archetype */}
            <div className="md:col-span-2 space-y-4">
              <h4 className="font-medium text-gray-900">Configuration</h4>
              
              {selectedArchetype === 'BOOKING' && (
                <div className="space-y-4">
                  <div>
                    <Label>Services (comma-separated)</Label>
                    <Input
                      placeholder="Haircut, Shave, Facial, Massage"
                      onChange={(e) => setConfigData({
                        ...configData,
                        services: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                      })}
                    />
                  </div>
                  <div>
                    <Label>Time Slots (comma-separated)</Label>
                    <Input
                      placeholder="9:00 AM, 10:00 AM, 11:00 AM, 2:00 PM"
                      onChange={(e) => setConfigData({
                        ...configData,
                        options: { ...configData.options, time_slots: e.target.value.split(',').map(s => s.trim()) }
                      })}
                    />
                  </div>
                </div>
              )}

              {selectedArchetype === 'PURCHASE' && (
                <div className="space-y-4">
                  <div>
                    <Label>Products (JSON format)</Label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      rows={4}
                      placeholder='[{"name": "Product 1", "price": 999}, {"name": "Product 2", "price": 1499}]'
                      onChange={(e) => {
                        try {
                          const products = JSON.parse(e.target.value);
                          setConfigData({ ...configData, products });
                        } catch {}
                      }}
                    />
                  </div>
                </div>
              )}

              {selectedArchetype === 'ENQUIRY' && (
                <div className="space-y-4">
                  <div>
                    <Label>Custom Form Fields (comma-separated)</Label>
                    <Input
                      placeholder="name, phone, email, message"
                      defaultValue="name, phone, email, message"
                      onChange={(e) => setConfigData({
                        ...configData,
                        options: { ...configData.options, enquiry_fields: e.target.value.split(',').map(s => s.trim()) }
                      })}
                    />
                  </div>
                </div>
              )}

              {selectedArchetype === 'SUPPORT' && (
                <div className="space-y-4">
                  <div>
                    <Label>Support Categories (comma-separated)</Label>
                    <Input
                      placeholder="General, Technical, Billing, Returns"
                      defaultValue="General, Technical, Billing, Returns"
                      onChange={(e) => setConfigData({
                        ...configData,
                        options: { ...configData.options, support_categories: e.target.value.split(',').map(s => s.trim()) }
                      })}
                    />
                  </div>
                </div>
              )}

              {/* Custom Labels */}
              <div>
                <Label>Welcome Title</Label>
                <Input
                  placeholder="Welcome to our service"
                  onChange={(e) => setConfigData({
                    ...configData,
                    labels: { ...configData.labels, title: e.target.value }
                  })}
                />
              </div>

              <div>
                <Label>Subtitle</Label>
                <Input
                  placeholder="How can we help you today?"
                  onChange={(e) => setConfigData({
                    ...configData,
                    labels: { ...configData.labels, subtitle: e.target.value }
                  })}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
            <Button 
              variant="outline" 
              onClick={() => { setShowCreateForm(false); resetForm(); }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateConfig}
              disabled={saving || !selectedSubcategory}
              className="bg-[#00D95F] hover:bg-[#00C054] gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Create Configuration
            </Button>
          </div>
        </Card>
      )}

      {/* Existing Configurations */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">
          Your Flow Configurations ({flowConfigs.length})
        </h3>

        {flowConfigs.length === 0 ? (
          <Card className="p-8 text-center bg-gray-50 border border-dashed border-gray-300">
            <Settings2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">No Configurations Yet</h4>
            <p className="text-gray-600 mb-4">Create your first WhatsApp Flow configuration to get started.</p>
            <Button 
              onClick={() => setShowCreateForm(true)}
              className="bg-[#00D95F] hover:bg-[#00C054]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Configuration
            </Button>
          </Card>
        ) : (
          <div className="grid gap-4">
            {flowConfigs.map(config => {
              const Icon = ARCHETYPE_ICONS[config.archetype];
              return (
                <Card key={config.id} className="p-4 bg-white border border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg ${ARCHETYPE_COLORS[config.archetype]}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{config.subcategory_name}</h4>
                        <p className="text-sm text-gray-500">{config.category}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`px-2 py-0.5 text-xs rounded-full ${ARCHETYPE_COLORS[config.archetype]}`}>
                            {config.archetype}
                          </span>
                          {ARCHETYPE_FLOW_TYPE[config.archetype] && (
                            <span className={`px-2 py-0.5 text-xs rounded-full ${ARCHETYPE_FLOW_TYPE[config.archetype].color}`}>
                              {ARCHETYPE_FLOW_TYPE[config.archetype].label}
                            </span>
                          )}
                          <span className={`px-2 py-0.5 text-xs rounded-full ${config.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {config.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyFlowToken(config.vendor_id, config.subcategory_id)}
                        title="Copy Flow Token"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUpdateConfig(config.id, config.subcategory_id, { is_active: !config.is_active })}
                      >
                        {config.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteConfig(config.subcategory_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                      <strong>Flow Token:</strong>{' '}
                      <code className="bg-gray-100 px-2 py-0.5 rounded">
                        vendor_{config.vendor_id}_subcat_{config.subcategory_id}
                      </code>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      <strong>Created:</strong> {new Date(config.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
